import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://engagedmanila.com';
const SITE_NAME = 'Engaged Manila';
const DEFAULT_IMAGE = '/logo512.png';
const DEFAULT_DESCRIPTION =
  'Engaged Manila connects brands with influencers for authentic product reviews. Browse campaigns, apply to collaborate, and grow your influence.';

/**
 * Reusable SEO component for per-page meta tags, Open Graph,
 * Twitter Cards, canonical URLs, and optional JSON-LD structured data.
 *
 * Props:
 *   title       - page title (will be appended with site name)
 *   description - meta description
 *   image       - OG/Twitter image (absolute or relative path)
 *   url         - canonical URL path (e.g. "/for-brands")
 *   type        - OG type ("website" | "article")
 *   noIndex     - if true, adds noindex directive
 *   jsonLd      - structured data object or array of objects
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url = '/',
  type = 'website',
  noIndex = false,
  jsonLd,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonicalUrl = `${SITE_URL}${url}`;
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      {/* Title & description */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* JSON-LD structured data */}
      {jsonLdArray.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </Helmet>
  );
};

export { SITE_URL, SITE_NAME };
export default SEO;
